import React, { useContext, useEffect, useState } from "react";

import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useNavigate } from "react-router-dom";

import moment from "moment";
import "moment-timezone";

import {
  getGameSystems,
  handlePaging
} from "../../components/api/APIUtilities";
import { mapData, stringToUrl } from "../events/utils/event";
import { normalizeEventV2Response } from "../events/components/EventFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import EventsOrganizeDefault from "./EventsOrganize/EventsOrganizeDefault";
import { signout } from "../../components/user/BCPAuth";
import {
  useProcessing,
  useSignout
} from "../../components/user/BCPAuthReducers";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { isAppMode } from "../../global/AppOverlay";
import { filterAndSortEvents } from "../../components/utilities/SortFilterFunctions";
import {
  getLocalStorage,
  setLocalStorage
} from "../../components/layout/cookies/CookieFunctions";
const SUPPORTS_BCP_PLATFORM_FEATURES =
  getEnvironmentVariable("SUPPORTS_BCP_PLATFORM_FEATURES") === "true";
const searchLimit = 24;

export default function EventsOrganize() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const appMode = isAppMode();

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState(null);
  const [filteredEvents, setFilteredEvents] = useState(null);
  const [gameSystems, setGameSystems] = useState(null);

  const [filters, setFilters] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [anchorEl3, setAnchorEl3] = useState(null);

  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [isSearching, setIsSearching] = useState(null);

  const [toggleMap, setToggleMapState] = useState(() => {
    const stored = getLocalStorage("eventsMapToggle", authContext);
    if (stored === undefined || stored === null) return false;
    if (stored === "true") return true;
    if (stored === "false") return false;
    return Boolean(stored);
  });

  useEffect(() => {
    setLocalStorage(
      "eventsMapToggle",
      toggleMap ? "true" : "false",
      authContext
    );
    // eslint-disable-next-line
  }, [toggleMap]);

  const setToggleMap = (val) => {
    setToggleMapState(val);
  };

  const [activeEvents, setActiveEvents] = useState(null);

  const [includeOrg, setIncludeOrg] = useState(true);

  const useSignoutReducer = useSignout();
  const useProcessingReducer = useProcessing();

  const search = async () => {
    let _gameSystems = gameSystems;

    if (!_gameSystems) {
      _gameSystems = await getGameSystems({
        inclSystem: true
      });
      setGameSystems(_gameSystems);
    }

    try {
      let gameSystemDict = {};
      _gameSystems.forEach((gameSystem) => {
        gameSystemDict[gameSystem.id] = gameSystem;
      });

      let queryStringParameters = {
        eventSearchType: "organizer",
        limit: searchLimit,
        sortKey: "eventDate",
        sortAscending: false,
        startDate: moment().subtract(1, "year").startOf("day").toISOString(),
        endDate: moment().add(1, "year").endOf("day").toISOString()
      };
      if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
        queryStringParameters.gameSystemId =
          getEnvironmentVariable("SINGLE_GAME_SYSTEM");
      } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
        queryStringParameters.gameType =
          getEnvironmentVariable("SINGLE_GAME_TYPE");
      } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
        queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
          "SINGLE_GAME_MANUFACTURER"
        );
      }

      var data = await handlePaging(
        "/events",
        queryStringParameters,
        nextKey,
        setNextKey,
        "v2"
      );

      data.forEach((event) => {
        Object.assign(event, normalizeEventV2Response(event));
      });

      data = mapData(data, gameSystemDict, moment);

      setEvents(data);

      if (data.length !== searchLimit) {
        setHasMore(false);
      }

      setEvents(events?.length ? [...events, ...data] : data);
      setIsSearching(false);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        try {
          if (SUPPORTS_BCP_PLATFORM_FEATURES) {
            navigate("/logout");
          } else {
            await signout(useSignoutReducer, {
              setProcessing: useProcessingReducer,
              navigate: navigate
            });
          }
        } catch (err) {
          postNotification("error", err);
        }
        navigate(`/login?redirect_url=${stringToUrl(window.location.href)}`);
        return;
      } else {
        postNotification("error", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = (eventId) => {
    var eventArray = [];
    if (events?.length) {
      events.forEach((event) => {
        if (event.id !== eventId) {
          eventArray.push(event);
        }
      });
    }
    setEvents(eventArray);
  };

  // Filter and sort events
  useEffect(() => {
    if (events?.length && filters.length) {
      const tempEvents = filterAndSortEvents(events, filters);

      setFilteredEvents(tempEvents);
    } else if (!filters.length) {
      setFilteredEvents(null);
    }
    // eslint-disable-next-line
  }, [filters, events]);

  useEffect(() => {
    const check = async () => {
      try {
        await search();
      } catch (err) {
        setEvents(null);

        if (err?.response?.status === 401 || err?.response?.status === 403) {
          try {
            if (SUPPORTS_BCP_PLATFORM_FEATURES) {
              navigate("/logout");
            } else {
              await signout(useSignoutReducer, {
                setProcessing: useProcessingReducer,
                navigate: navigate
              });
            }
          } catch (err) {
            postNotification("error", err);
          }
          navigate(`/login?redirect_url=${stringToUrl(window.location.href)}`);
          return;
        } else {
          postNotification("error", err);
        }
      } finally {
        setLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!authContext?.authState?.identity?.userId) {
      navigate(`/login?redirect_url=${stringToUrl(window.location.href)}`);
      return;
    }
    // eslint-disable-next-line
  }, []);

  return (
    <EventsOrganizeDefault
      loading={loading}
      hasMore={hasMore}
      isSearching={isSearching}
      deleteEvent={deleteEvent}
      search={search}
      events={events}
      authContext={authContext}
      includeOrg={includeOrg}
      setIncludeOrg={setIncludeOrg}
      appMode={appMode}
      filters={filters}
      setFilters={setFilters}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      anchorEl2={anchorEl2}
      setAnchorEl2={setAnchorEl2}
      anchorEl3={anchorEl3}
      setAnchorEl3={setAnchorEl3}
      filteredEvents={filteredEvents}
      setFilteredEvents={setFilteredEvents}
      gameSystems={gameSystems}
      setToggleMap={setToggleMap}
      toggleMap={toggleMap}
      activeEvents={activeEvents}
      setActiveEvents={setActiveEvents}
    />
  );
}
